import isEqual from 'utils/isEqual'

import { EXCHANGES, getDataSourceDisplayName } from 'constants/sources/dataSources'
import DataSource from 'constants/sources/DataSource'
import { IN, OUT, INTERNAL, EXTERNAL, SWAP } from 'constants/TransactionTypes'

import { canonical } from 'utils/Converters'
import getBaseTxnAndFiatValues, {
  getTxnOverrideValue,
} from 'utils/transactions/getBaseTxnAndFiatValues'
import isTransactionSyncEligible from 'utils/accounting/isTransactionSyncEligible'

export const epsilon = 0.000000001

export function abbreviateAddress(addr, previewLength = 4) {
  return addr === undefined
    ? ''
    : addr.length <= 2 * previewLength + 3
      ? addr
      : addr.substring(0, previewLength) + '...' + addr.substring(addr.length - previewLength)
}

export function generateAssetDisplayName(name, tokenId, previewLength = 4) {
  let display = abbreviateAddress(name, previewLength)

  if (tokenId) {
    if (tokenId.length > 10) {
      display += ` [${tokenId.substring(0, 10)}...]`
    } else {
      display += ` [${tokenId}]`
    }
  }

  return display
}

export function getInsAndOutsDisplayTxnAndFiatValues(txn, wallets, txnValueOverrides = {}) {
  // returns in shape of [InAssetAmount, InFiatValue, InAssets, OutAssetAmount, OutFiatValue, OutAssets]
  // uses undefined if In or Out does not exist for transaction
  //  we surface all of the ins and outs in cases of sending or receiving multiple tokens
  if (!txn || !txn.toAddress || !txn.groupTransactionType || !wallets) {
    return [undefined, 0, [], undefined, 0, []]
  }

  const walletsSet = new Set(wallets.map((w) => canonical(w.addr)))

  if (txn.groupTransactionType === IN || txn.groupTransactionType === OUT) {
    const displayValues = getDisplayTxnAndFiatValues(txn, txnValueOverrides)
    if (txn.groupTransactionType === IN) {
      return [displayValues[0], displayValues[1], displayValues[2], undefined, 0, []]
    } else {
      return [undefined, 0, [], displayValues[0], displayValues[1], displayValues[2]]
    }
  }

  // coinbase internal transfers don't usually align with the address approach
  if (
    (EXCHANGES.includes(txn.dataSource.source) || txn.dataSource.source === DataSource.CsvImport) &&
    (txn.groupTransactionType === SWAP || txn.groupTransactionType === INTERNAL)
  ) {
    // for some exchanges, like Coinbase Prime, we only have one transaction going from A->B
    //  we want it to appear in the In and Out sections though
    if (EXCHANGES.includes(txn.dataSource.source) && !txn.group.length) {
      return [
        txn.value.value.amount,
        getTxnOverrideValue(txn, txnValueOverrides),
        [{ asset: txn.value.value.asset, contractAddress: txn.value.value.contractAddress }],
        txn.value.value.amount,
        getTxnOverrideValue(txn, txnValueOverrides),
        [{ asset: txn.value.value.asset, contractAddress: txn.value.value.contractAddress }],
      ]
    } else if (txn.transactionType === IN || txn.transactionType === INTERNAL) {
      return [
        txn.value.value.amount,
        getTxnOverrideValue(txn, txnValueOverrides),
        [{ asset: txn.value.value.asset, contractAddress: txn.value.value.contractAddress }],
        txn.group[0]?.value.value.amount,
        getTxnOverrideValue(txn.group[0], txnValueOverrides),
        [
          {
            asset: txn.group[0]?.value.value.asset,
            contractAddress: txn.group[0]?.value.value.contractAddress,
          },
        ],
      ]
    } else {
      return [
        txn.group[0]?.value.value.amount,
        getTxnOverrideValue(txn.group[0], txnValueOverrides),
        [
          {
            asset: txn.group[0]?.value.value.asset,
            contractAddress: txn.group[0]?.value.value.contractAddress,
          },
        ],
        txn.value.value.amount,
        getTxnOverrideValue(txn, txnValueOverrides),
        [{ asset: txn.value.value.asset, contractAddress: txn.value.value.contractAddress }],
      ]
    }
  }

  // go through group and main transaction and compile ins and outs
  //  dictionaries where asset is key and (amount, usdValue) is value, so we can sum up usdValues to find the max
  const ins = {}
  const outs = {}
  const inAssetNames = []
  const outAssetNames = []
  txn.group.concat(txn).forEach((t) => {
    if (t.transactionType === EXTERNAL) {
      return
    }

    const asset = t.value.value.asset
    const contractAddress = t.value.value.contractAddress
    const amount = t.value.value.amount
    const fiatAmount = getTxnOverrideValue(t, txnValueOverrides)

    // Should probably replace this with looking at transactionType?
    if (
      t.fromAddress &&
      isInternalAddress(walletsSet, t.fromAddress) &&
      asset &&
      [OUT, INTERNAL, SWAP].includes(t.transactionType)
    ) {
      if (asset in outs) {
        outs[asset] = [outs[asset][0] + amount, outs[asset][1] + fiatAmount]
      } else {
        outs[asset] = [amount, fiatAmount]
      }

      if (amount > 0 && !outAssetNames.some((x) => x.contractAddress === contractAddress)) {
        outAssetNames.push({ asset, contractAddress })
      }
    }

    if (
      t.toAddress &&
      isInternalAddress(walletsSet, t.toAddress) &&
      asset &&
      [IN, INTERNAL, SWAP].includes(t.transactionType)
    ) {
      if (asset in ins) {
        ins[asset] = [ins[asset][0] + amount, ins[asset][1] + fiatAmount]
      } else {
        ins[asset] = [amount, fiatAmount]
      }

      if (amount > 0 && !inAssetNames.some((x) => x.contractAddress === contractAddress)) {
        inAssetNames.push({ asset, contractAddress })
      }
    }
  })

  const maxInAsset = findMaxFiatValueForAsset(ins)
  const maxOutAsset = findMaxFiatValueForAsset(outs)
  const insSumFiatValue = Object.entries(ins).reduce((prev, current) => prev + current[1][1], 0)
  const outsSumFiatValue = Object.entries(outs).reduce((prev, current) => prev + current[1][1], 0)

  return [
    (ins[maxInAsset] || [0, 0])[0] /* inAmount */,
    insSumFiatValue /* inFiat */,
    inAssetNames /* inAssets */,
    (outs[maxOutAsset] || [0, 0])[0] /* outAmount */,
    outsSumFiatValue /* outFiat */,
    outAssetNames /* outAssets */,
  ]
}

/**
 * Checks if an address is an internal address by checking if it is in the wallet set or if it has a parent address in the wallet set (xPub)
 */
function isInternalAddress(walletSet, address) {
  return (
    walletSet.has(canonical(address.addr)) ||
    (address.parent && walletSet.has(canonical(address.parent)))
  )
}

export function findMaxFiatValueForAsset(dict) {
  // input: dict where asset is key and (amount, usdValue) is value
  // returns: key with max USD value
  let max = -Infinity
  let maxKey = null
  Object.keys(dict).forEach((key) => {
    if (dict[key][1] > max) {
      max = dict[key][1]
      maxKey = key
    }
  })

  return maxKey
}

// Uses the sum of usd amounts and list of assets for display purposes,
// or otherwise the main transaction.
export function getDisplayTxnAndFiatValues(txn, txnValueOverrides) {
  if (!txn || !txn.toAddress) {
    return [0.0, 0.0, []]
  }

  if (txn.group?.length > 0) {
    const [sumAmount, sumFiatValue] = txn.group.concat(txn).reduce(
      ([accValue, accFiatValue], current) => {
        if (current.transactionType === EXTERNAL) {
          return [accValue, accFiatValue]
        }
        const { value: txnValue } = getBaseTxnAndFiatValues(current)
        const txnFiatValue = getTxnOverrideValue(current, txnValueOverrides)
        return [accValue + txnValue, accFiatValue + txnFiatValue]
      },
      [0, 0]
    )
    const allAssets = txn.group
      .concat(txn)
      .filter((t) => t.transactionType !== EXTERNAL && t.value.value.amount > 0)
      .reduce((curr, txn) => {
        if (!curr.some((asset) => asset.contractAddress === txn.value.value.contractAddress)) {
          curr.push({
            asset: txn.value.value.asset,
            contractAddress: txn.value.value.contractAddress,
          })
        }
        return curr
      }, [])

    return [allAssets.length === 1 ? sumAmount : 0.0, sumFiatValue, allAssets]
  }

  const { value: txnValue, asset } = getBaseTxnAndFiatValues(txn)
  const txnFiatValue = getTxnOverrideValue(txn, txnValueOverrides)

  return [txnValue, txnFiatValue, [asset]]
}

export function truncateToLength(str, length) {
  return str !== undefined ? (str.length > length ? str.substring(0, length) + '...' : str) : ''
}

function isEmpty(str) {
  return !str || str.length === 0
}

export function getTransactionAddressDisplayValue(address) {
  return address.label === 'Address'
    ? address.addr
    : !isEmpty(address.addr)
      ? address.label + ' - ' + address.addr
      : address.label
}

export function shouldDisplayRelevantAddresses(txnList) {
  if (txnList.length !== 2) {
    return false
  }

  const [txn1, txn2] = txnList

  if (!txn1 || !txn2) {
    return false
  }

  return (
    isEqual([txn1.transactionType, txn2.transactionType].sort(), [IN, OUT]) &&
    txn1.value.value.asset === txn2.value.value.asset &&
    txn1.value.value.amount > 0 &&
    Math.abs(txn1.value.value.amount - txn2.value.value.amount) < epsilon
  )
}

export function getDisplayToAndFromAddress(txn) {
  let toAddr = ''
  let toName = ''
  let fromAddr = ''
  let fromName = '' // default to empty string
  const mainTransactionToAddress = txn.toAddress.addr
  const mainTransactionToName = txn.toAddress.label
  const mainTransactionFromAddress = txn.fromAddress.addr
  const mainTransactionFromName = txn.fromAddress.label

  if (txn.group && Array.isArray(txn.group) && txn.group.length > 0) {
    // parent transaction with nested transactions, use the main transaction
    // addresses if available, otherwise use the first nested transaction
    toAddr = mainTransactionToAddress || txn.group[0].toAddress.addr
    toName = mainTransactionToName || txn.group[0].toAddress.label
    fromAddr = mainTransactionFromAddress || txn.group[0].fromAddress.addr
    fromName = mainTransactionFromName || txn.group[0].fromAddress.label
  } else {
    // nested transaction or parent with no nested transactions
    toAddr = mainTransactionToAddress
    toName = mainTransactionToName
    fromAddr = mainTransactionFromAddress
    fromName = mainTransactionFromName
  }
  return { toAddr, toName, fromAddr, fromName }
}

export function getDisplayDataSourceLabel(txn) {
  const isCsvSource = txn.dataSource.source === DataSource.CsvImport

  if (isCsvSource) {
    return txn.dataSource.label
  } else {
    return getDataSourceDisplayName(txn.dataSource.source)
  }
}

export function validateFiatRangeInput(newRange, setIsMinInputValid, setIsMaxInputValid) {
  const currMin = newRange[0]
  const currMax = newRange[1]
  if (currMin < 0) {
    setIsMinInputValid(false)
    return
  }
  if (currMax < 0) {
    setIsMaxInputValid(false)
    return
  }
  if (currMin !== null && currMax !== null && currMax < currMin) {
    setIsMinInputValid(false)
    setIsMaxInputValid(false)
    return
  }

  setIsMinInputValid(true)
  setIsMaxInputValid(true)
}

export function allTransactionsSyncEligible(txns, accountingConfig) {
  return txns.every((txn) => {
    return isTransactionSyncEligible(txn, accountingConfig)
  })
}

export function transactionCostShouldBeHidden(txn) {
  const hasNonZeroFeeAmount = txn.value.transactionCost?.amount > 0

  // Only return true (hide fee) if:
  // 1. There is no fee amount
  //   a. If a user supplies a fee amount in CSV, we should show it
  //   b. For BELT (IaaS) transactions, any fee dropping or including is expected to be handled in the TSS parser
  return !hasNonZeroFeeAmount
}

export function getLabelFromTxnName(addr, name) {
  let label = name || <i>Contract</i>
  if ((!label || label === 'Address') && addr) {
    label = abbreviateAddress(addr, 4)
  }

  let tooltip = ''
  if (label.length > 25) {
    tooltip = label
    label = label.substring(0, 25) + '...'
  }

  return { label, tooltip }
}

export function getBulkTransactionProperties(txns) {
  const definedTxns = txns.filter((txn) => txn !== undefined)
  const txnCategories = new Set(definedTxns.map((txn) => txn.annotation.category))
  const txnTypes = new Set(definedTxns.map((txn) => txn.groupTransactionType))
  const txnMemos = new Set(definedTxns.map((txn) => txn.annotation.memo))

  return { txnCategories, txnTypes, txnMemos }
}
